import api from './api';
import { ApiResponse, PaginatedResponse } from '../types';
import {
  GeneratedVideo,
  VideoCreditBalance,
  VideoCreditTransaction,
  VideoEstimate,
  VideoGenerateParams,
  HeygenAvatar,
  HeygenVoice,
  ProviderStatus,
  CreditPackId,
  CreditPack
} from '../types/videoStudio';
import { pollJobUntilComplete } from './aiJobService';

interface VideoFilters {
  page?: number;
  limit?: number;
  provider?: string;
  status?: string;
  campaign_id?: string;
  // 'none' = vídeos sem pasta. UUID = pasta específica. undefined = todos.
  folder_id?: string | 'none';
  is_favorite?: 'true' | 'false';
  search?: string;
}

export const videoStudioService = {
  async listVideos(filters?: VideoFilters): Promise<PaginatedResponse<GeneratedVideo>> {
    const { data } = await api.get('/video-studio', { params: filters });
    return data;
  },

  async getVideo(id: string): Promise<ApiResponse<GeneratedVideo>> {
    const { data } = await api.get(`/video-studio/${id}`);
    return data;
  },

  async estimate(params: Partial<VideoGenerateParams> & { duration_seconds?: number; scenes?: number }): Promise<ApiResponse<VideoEstimate>> {
    // Backend's /estimate accepts a flat shape (provider, duration_seconds, resolution, mode, scenes, avatar_tier)
    const payload = {
      provider: params.provider || 'seedance',
      duration_seconds: (params as any).duration_seconds || params.parameters?.duration || 5,
      resolution: params.parameters?.resolution || '720p',
      mode: params.mode || 'text2video',
      scenes: typeof (params as any).scenes === 'number' ? (params as any).scenes : (params.scenes?.length || 0),
      avatar_tier: params.parameters?.avatar_tier || 'standard',
      // Cada modelo do Seedance tem preço próprio por segundo
      model: params.parameters?.model
    };
    const { data } = await api.post('/video-studio/estimate', payload);
    return data;
  },

  /**
   * Generate a video. Always async — polls until done.
   */
  async generate(
    params: VideoGenerateParams,
    onProgress?: (progress: number) => void
  ): Promise<ApiResponse<GeneratedVideo>> {
    const { data } = await api.post('/video-studio/generate', params);

    if (data.async && data.jobId) {
      const videoId = data.data?.id;
      if (!videoId) throw new Error('Server did not return video id');

      await pollJobUntilComplete(
        data.jobId,
        (pct) => { if (onProgress) onProgress(pct); },
        3000,
        20 * 60 * 1000 // 20 min — video can be slow
      );

      // Fetch final record
      for (let attempt = 0; attempt < 5; attempt++) {
        const fresh = await api.get(`/video-studio/${videoId}`);
        const v: GeneratedVideo = fresh.data?.data;
        if (v?.status === 'failed') throw new Error(v.error_message || 'Video generation failed');
        if (v?.original_url) return fresh.data;
        await new Promise(r => setTimeout(r, 1500));
      }
      throw new Error('Video generation completed but no URL returned');
    }
    return data;
  },

  async uploadComposition(
    file: Blob,
    meta: {
      title?: string;
      aspect_ratio?: string;
      duration?: number;
      scenes?: any[];
      thumbnail_url?: string;
      campaign_id?: string;
      // Parâmetros de qualidade escolhidos pelo user no modal de export.
      // Vão pra parameters.* do GeneratedVideo no backend pra auditoria/replay.
      resolution?: '720p' | '1080p' | '4k';
      bitrate?: number;
      fps?: 24 | 30 | 60;
      format?: 'webm' | 'webm-vp9' | 'mp4';
    },
    onProgress?: (pct: number) => void
  ): Promise<ApiResponse<GeneratedVideo>> {
    const formData = new FormData();
    // Nome do arquivo segue o container REAL do blob — o MediaRecorder cai pra
    // WebM quando o browser não grava MP4, e um "composition.mp4" com bytes WebM
    // era recusado depois na publicação (Instagram/TikTok validam o container).
    const ext = /webm/i.test(file.type || '') ? 'webm' : 'mp4';
    formData.append('file', file, `composition.${ext}`);
    if (meta.title) formData.append('title', meta.title);
    if (meta.aspect_ratio) formData.append('aspect_ratio', meta.aspect_ratio);
    if (meta.duration) formData.append('duration', String(meta.duration));
    if (meta.scenes) formData.append('scenes', JSON.stringify(meta.scenes));
    if (meta.thumbnail_url) formData.append('thumbnail_url', meta.thumbnail_url);
    if (meta.campaign_id) formData.append('campaign_id', meta.campaign_id);
    if (meta.resolution) formData.append('resolution', meta.resolution);
    if (meta.bitrate !== undefined) formData.append('bitrate', String(meta.bitrate));
    if (meta.fps !== undefined) formData.append('fps', String(meta.fps));
    if (meta.format) formData.append('format', meta.format);

    const { data } = await api.post('/video-studio/upload-composition', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 30 * 60 * 1000, // 30min — large compositions can be 100MB+
      onUploadProgress: (e) => {
        if (onProgress && e.total) onProgress(Math.round((e.loaded * 100) / e.total));
      }
    });
    return data;
  },

  /**
   * Render server-side da timeline (ffmpeg na fila). Muito mais rápido que o
   * render no navegador, sai sempre MP4/H.264 e o usuário pode fechar a aba —
   * o vídeo aparece na galeria quando terminar.
   */
  async renderOnServer(
    payload: {
      timeline: {
        videoClips: any[]; audioClips: any[]; overlayClips: any[];
        captionTracks: any[]; aspectRatio: string;
      };
      resolution?: '720p' | '1080p' | '4k';
      fps?: 24 | 30 | 60;
      bitrate?: number;
      title?: string;
      campaign_id?: string;
    },
    onProgress?: (pct: number) => void
  ): Promise<ApiResponse<GeneratedVideo>> {
    const { data } = await api.post('/video-studio/render', payload);
    if (data.async && data.jobId) {
      const videoId = data.data?.id;
      if (!videoId) throw new Error('Server did not return video id');
      await pollJobUntilComplete(
        data.jobId,
        (pct) => { if (onProgress) onProgress(pct); },
        3000,
        30 * 60 * 1000 // renders longos na fila podem esperar vez
      );
      for (let attempt = 0; attempt < 5; attempt++) {
        const fresh = await api.get(`/video-studio/${videoId}`);
        const v: GeneratedVideo = fresh.data?.data;
        if (v?.status === 'failed') throw new Error(v.error_message || 'Render no servidor falhou');
        if (v?.original_url) return fresh.data;
        await new Promise(r => setTimeout(r, 1500));
      }
      throw new Error('Render terminou mas o vídeo não retornou URL');
    }
    return data;
  },

  async uploadImage(file: File): Promise<{ url: string }> {
    const formData = new FormData();
    formData.append('file', file);
    const { data } = await api.post('/video-studio/upload-image', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return data?.data;
  },

  async update(id: string, payload: { title?: string; tags?: string[]; is_favorite?: boolean; campaign_id?: string | null; thumbnail_url?: string | null }): Promise<ApiResponse<GeneratedVideo>> {
    const { data } = await api.put(`/video-studio/${id}`, payload);
    return data;
  },

  async remove(id: string): Promise<ApiResponse<void>> {
    const { data } = await api.delete(`/video-studio/${id}`);
    return data;
  },

  async getCredits(): Promise<ApiResponse<VideoCreditBalance>> {
    const { data } = await api.get('/video-studio/credits');
    return data;
  },

  async getCreditTransactions(limit = 20): Promise<ApiResponse<VideoCreditTransaction[]>> {
    const { data } = await api.get('/video-studio/credits/transactions', { params: { limit } });
    return data;
  },

  async listCreditPacks(): Promise<ApiResponse<Record<CreditPackId, CreditPack>>> {
    const { data } = await api.get('/video-studio/credits/packs');
    return data;
  },

  async purchase(pack: CreditPackId, payment_intent_id?: string): Promise<ApiResponse<VideoCreditBalance>> {
    const { data } = await api.post('/video-studio/credits/purchase', { pack, payment_intent_id });
    return data;
  },

  // Start Stripe Checkout for a video credit pack — returns the URL to redirect to.
  async startCreditCheckout(pack: CreditPackId): Promise<ApiResponse<{ url: string; session_id: string }>> {
    const { data } = await api.post('/video-studio/credits/checkout', { pack });
    return data;
  },

  // Confirm a completed Stripe Checkout Session and credit the pack to the user.
  // Idempotent: calling it twice with the same session_id will not double-credit.
  async confirmCreditCheckout(session_id: string): Promise<ApiResponse<{ balance: VideoCreditBalance; already_credited: boolean }>> {
    const { data } = await api.post('/video-studio/credits/confirm', { session_id });
    return data;
  },

  // Generate a video prompt or avatar script tailored to the chosen engine,
  // using the user's brand profile so the wording matches their voice.
  async aiEdit(payload: {
    prompt: string;
    timeline: {
      videoClips: any[];
      audioClips: any[];
      overlayClips: any[];
      captionTracks: any[];
      aspectRatio: string;
    };
  }): Promise<ApiResponse<{
    timeline: {
      videoClips: any[];
      audioClips: any[];
      overlayClips: any[];
      captionTracks: any[];
      aspectRatio: string;
    };
    summary: string;
    toolCalls: Array<{ name: string; input: any; ok: boolean; result?: string; error?: string }>;
    tokensUsed: number;
    iterations: number;
    balance: VideoCreditBalance;
  }>> {
    const { data } = await api.post('/video-studio/ai-edit', payload);
    return data;
  },

  async aiPrompt(payload: { engine: 'seedance' | 'heygen'; topic?: string; language?: string; currentText?: string }): Promise<ApiResponse<{ text: string; engine: string; language?: string }>> {
    const { data } = await api.post('/video-studio/ai-prompt', payload);
    return data;
  },

  async listAvatars(params?: { page?: number; limit?: number; search?: string; owned?: 'only' | 'stock' }): Promise<{ success: boolean; data: HeygenAvatar[]; total: number; page: number; limit: number; totalPages: number; configured: boolean }> {
    const { data } = await api.get('/video-studio/heygen/avatars', { params });
    return data;
  },

  // Backend-grouped avatars: returns ONE entry per person (group), not per look.
  // Each entry has cover + look_count. Lazy-load looks via listAvatarGroupLooks.
  async listAvatarGroups(params?: { page?: number; limit?: number; search?: string; owned?: 'only' | 'stock' }): Promise<{
    success: boolean;
    data: { key: string; name: string; look_count: number; cover_id: string; preview_image_url?: string; preview_video_url?: string; gender?: string; premium: boolean; is_yours: boolean; is_custom: boolean }[];
    total: number; page: number; limit: number; totalPages: number; configured: boolean;
  }> {
    const { data } = await api.get('/video-studio/heygen/avatar-groups', { params });
    return data;
  },

  async listAvatarGroupLooks(key: string): Promise<{ success: boolean; data: HeygenAvatar[]; configured: boolean }> {
    const { data } = await api.get(`/video-studio/heygen/avatar-groups/${encodeURIComponent(key)}/looks`);
    return data;
  },

  async listVoices(params?: { page?: number; limit?: number; search?: string; language?: string }): Promise<{ success: boolean; data: HeygenVoice[]; total: number; page: number; limit: number; totalPages: number; configured: boolean }> {
    const { data } = await api.get('/video-studio/heygen/voices', { params });
    return data;
  },

  // Lightweight: returns just languages + counts so the UI can build the
  // language picker without loading 2k voices upfront.
  async listVoiceLanguages(): Promise<{ success: boolean; data: { language: string; count: number }[]; configured: boolean }> {
    const { data } = await api.get('/video-studio/heygen/voice-languages');
    return data;
  },

  async providerStatus(): Promise<ApiResponse<ProviderStatus>> {
    const { data } = await api.get('/video-studio/providers/status');
    return data;
  },

  /**
   * TTS preview — generates spoken audio without burning a full HeyGen video credit.
   * Costs only ~1 credit so users can iterate on the voice/script before rendering.
   */
  // Returns the voice's stock preview_audio URL — free, no credits charged.
  async previewVoice(payload: { voice_id: string }): Promise<ApiResponse<{ audio_url: string; duration?: number; script_synthesized?: boolean }>> {
    const { data } = await api.post('/video-studio/heygen/preview-voice', payload);
    return data;
  },

  // Create a Digital Twin avatar with optional ElevenLabs voice clone.
  // Backend now returns a job_id immediately (uploads + HeyGen + ElevenLabs run
  // async in setImmediate to avoid Cloudflare's 100s edge timeout). We poll
  // GET /heygen/avatar-jobs/:id every 4s until the job is complete.
  async createAvatar(payload: {
    name: string;
    gender?: string;
    language?: string;
    trainingVideo: File;
    consentVideo: File;
    voiceAudio?: File | null;
  }, onProgress?: (pct: number, step?: string) => void): Promise<ApiResponse<{ avatar: { heygen_id: string; status: string }; voice: { elevenlabs_id?: string; status?: string; error?: string } | null }>> {
    const fd = new FormData();
    fd.append('name', payload.name);
    if (payload.gender) fd.append('gender', payload.gender);
    if (payload.language) fd.append('language', payload.language);
    fd.append('training_video', payload.trainingVideo);
    fd.append('consent_video', payload.consentVideo);
    if (payload.voiceAudio) fd.append('voice_audio', payload.voiceAudio);

    // Submit (the multipart upload itself can take a while — long timeout)
    const { data: submit } = await api.post('/video-studio/heygen/avatars', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 30 * 60 * 1000,
      onUploadProgress: (e) => {
        // 0-50% reserved for the upload phase, server reports 50%+ for processing
        if (onProgress && e.total) onProgress(Math.round((e.loaded / e.total) * 50), 'uploading');
      }
    });
    if (!submit.success || !submit.data?.job_id) return submit;
    const jobId = submit.data.job_id;

    // Poll until done (15min cap, 4s interval)
    const deadline = Date.now() + 15 * 60 * 1000;
    while (Date.now() < deadline) {
      await new Promise(r => setTimeout(r, 4000));
      const { data: status } = await api.get(`/video-studio/heygen/avatar-jobs/${jobId}`);
      if (!status.success) throw new Error(status.message || 'Avatar job poll failed');
      if (onProgress) onProgress(50 + Math.floor((status.data.progress || 0) / 2), status.data.step);
      if (status.data.status === 'completed') return { success: true, data: status.data.result };
      if (status.data.status === 'failed') throw new Error(status.data.error || 'Avatar creation failed');
    }
    throw new Error('Avatar creation timed out (15min)');
  },

  async deleteAvatar(heygenAvatarId: string): Promise<ApiResponse<void>> {
    const { data } = await api.delete(`/video-studio/heygen/avatars/${heygenAvatarId}`);
    return data;
  },

  async getAvatarStatus(id: string): Promise<ApiResponse<{ id: string; status: string; preview_image_url?: string; preview_video_url?: string }>> {
    const { data } = await api.get(`/video-studio/heygen/avatars/${id}/status`);
    return data;
  },

  async cloneVoice(payload: { name: string; audio_url: string; language?: string }): Promise<ApiResponse<{ voice_id: string; status: string }>> {
    const { data } = await api.post('/video-studio/heygen/voices/clone', payload);
    return data;
  },

  async deleteVoice(heygenVoiceId: string): Promise<ApiResponse<void>> {
    const { data } = await api.delete(`/video-studio/heygen/voices/${heygenVoiceId}`);
    return data;
  },

  async generateMultiSceneHeygen(
    payload: {
      scenes: { script: string; avatarId: string; voiceId: string; background?: string; voiceSpeed?: number; avatarStyle?: string }[];
      aspect_ratio?: string;
      title?: string;
      campaign_id?: string;
    },
    onProgress?: (progress: number) => void
  ): Promise<ApiResponse<GeneratedVideo>> {
    const { data } = await api.post('/video-studio/heygen/generate-multiscene', payload);
    if (data.async && data.jobId) {
      const videoId = data.data?.id;
      await pollJobUntilComplete(
        data.jobId,
        (pct) => { if (onProgress) onProgress(pct); },
        3000,
        20 * 60 * 1000
      );
      for (let i = 0; i < 5; i++) {
        const fresh = await api.get(`/video-studio/${videoId}`);
        const v: GeneratedVideo = fresh.data?.data;
        if (v?.status === 'failed') throw new Error(v.error_message || 'Multi-scene generation failed');
        if (v?.original_url) return fresh.data;
        await new Promise(r => setTimeout(r, 1500));
      }
      throw new Error('Multi-scene generation completed but no URL returned');
    }
    return data;
  },

  async uploadAudio(file: File): Promise<{ url: string }> {
    const formData = new FormData();
    formData.append('file', file);
    const { data } = await api.post('/video-studio/upload-audio', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return data?.data;
  },

  // Auto-transcribe audio of a video using Whisper. Backend now runs the work
  // async (Whisper can take 30-120s, beyond Cloudflare's 100s edge timeout) so
  // we poll: POST returns { job_id }, then GET /transcribe/:job_id until done.
  async transcribe(
    payload: { source_url?: string; video_id?: string; language?: string },
    onProgress?: (progress: number) => void
  ): Promise<ApiResponse<{
    duration: number;
    language?: string;
    full_text: string;
    segments: { start: number; end: number; text: string }[];
    credits_charged: number;
  }>> {
    // Step 1 — submit and get job_id
    const { data: submitResp } = await api.post('/video-studio/transcribe', payload);
    if (!submitResp.success || !submitResp.data?.job_id) return submitResp;
    const jobId = submitResp.data.job_id;

    // Step 2 — poll until completed/failed (15min cap, 3s interval)
    const pollInterval = 3000;
    const timeoutMs = 15 * 60 * 1000;
    const deadline = Date.now() + timeoutMs;
    while (Date.now() < deadline) {
      await new Promise(r => setTimeout(r, pollInterval));
      const { data: status } = await api.get(`/video-studio/transcribe/${jobId}`);
      if (!status.success) throw new Error(status.message || 'Transcription poll failed');
      if (typeof onProgress === 'function' && typeof status.data?.progress === 'number') {
        onProgress(status.data.progress);
      }
      if (status.data.status === 'completed') return { success: true, data: status.data.result };
      if (status.data.status === 'failed') throw new Error(status.data.error || 'Transcription failed');
    }
    throw new Error('Transcription timed out (15min)');
  },

  // Editor projects (save / load timeline state)
  async listProjects(isTemplate?: boolean): Promise<ApiResponse<any[]>> {
    const { data } = await api.get('/video-studio/projects', { params: isTemplate ? { is_template: 'true' } : {} });
    return data;
  },

  async getProject(id: string): Promise<ApiResponse<any>> {
    const { data } = await api.get(`/video-studio/projects/${id}`);
    return data;
  },

  async saveProject(payload: {
    id?: string; name: string; thumbnail_url?: string; aspect_ratio?: string;
    duration_seconds?: number; data: any; is_template?: boolean;
  }): Promise<ApiResponse<any>> {
    const { data } = await api.post('/video-studio/projects', payload);
    return data;
  },

  async deleteProject(id: string): Promise<ApiResponse<void>> {
    const { data } = await api.delete(`/video-studio/projects/${id}`);
    return data;
  },

  // Register an external asset URL (e.g. Image Studio image, existing S3 file) into
  // the user's Video Studio library — no re-upload, just creates a GeneratedVideo
  // pointing to the same URL so it appears in the MediaLibrary.
  async importAsset(payload: {
    url: string; title?: string; mime_type?: string;
    duration?: number; width?: number; height?: number; aspect_ratio?: string;
  }): Promise<ApiResponse<GeneratedVideo>> {
    const { data } = await api.post('/video-studio/import-asset', payload);
    return data;
  },

  async uploadOwnVideo(file: File, meta?: { title?: string; aspect_ratio?: string; duration?: number; campaign_id?: string }): Promise<ApiResponse<GeneratedVideo>> {
    const formData = new FormData();
    formData.append('file', file);
    if (meta?.title) formData.append('title', meta.title);
    if (meta?.aspect_ratio) formData.append('aspect_ratio', meta.aspect_ratio);
    if (meta?.duration) formData.append('duration', String(meta.duration));
    if (meta?.campaign_id) formData.append('campaign_id', meta.campaign_id);
    const { data } = await api.post('/video-studio/upload-video', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return data;
  }
};
